import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function HandoverScanner() {
  const [barcode, setBarcode] = useState('');
  const [lotData, setLotData] = useState(null);
  
  // Modes: 'scan', 'outgoing', 'incoming', 'ticket'
  const [mode, setMode] = useState('scan');
  
  // Form States
  const [outgoingQty, setOutgoingQty] = useState('');
  const [incomingQty, setIncomingQty] = useState('');
  const [qualityStatus, setQualityStatus] = useState('Pass');
  
  // Ticket States
  const [defectCategory, setDefectCategory] = useState('');
  const [comments, setComments] = useState('');
  const [severity, setSeverity] = useState('Minor');
  const [photos, setPhotos] = useState([]);

  const fetchLot = async (e) => {
    if (e) e.preventDefault();
    if (!barcode.trim()) return Swal.fire("Warning", "Please enter a barcode.", "warning");

    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/lots-barcode/${barcode}`, { withCredentials: true });
      if (res.data.success) {
        setLotData(res.data.data);
        if (res.data.data.status === 'Pending' || res.data.data.status === 'In Progress') {
           setMode('outgoing');
        } else if (res.data.data.status === 'Awaiting Handover') {
           setMode('incoming');
        } else {
           Swal.fire("Invalid Status", "Lot is not ready for handover. Current Status: " + res.data.data.status, "info");
        }
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Lot not found.", "error");
      setLotData(null);
    }
  };

  const handleOutgoingSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        lot_id: lotData.lot_id,
        process_id: lotData.current_process_id,
        outgoing_declared_qty: outgoingQty
      };
      
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/handovers/outgoing`, payload, { withCredentials: true });
      if (res.data.success) {
        Swal.fire("Success", "Handover initiated! Waiting for receiving department.", "success");
        resetForm();
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Error transferring lot.", "error");
    }
  };

  const handleIncomingSubmit = async (e) => {
    e.preventDefault();
    if (qualityStatus === 'Fail' || (incomingQty && parseInt(incomingQty) !== parseInt(lotData.planned_qty))) {
        setMode('ticket');
        return; 
    }
    try {
      const payload = {
        lot_id: lotData.lot_id,
        process_id: lotData.next_process_id,
        received_qty: incomingQty,
        quality_status: qualityStatus
      };
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/handovers/receive`, payload, { withCredentials: true });
      if (res.data.success) {
        Swal.fire("Success", "Handover received successfully!", "success");
        resetForm();
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Error receiving lot.", "error");
    }
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    if (photos.length === 0) return Swal.fire("Required", "Photo evidence is mandatory for a discrepancy ticket.", "warning");

    const formData = new FormData();
    formData.append('lot_id', lotData.lot_id);
    formData.append('process_id', lotData.current_process_id);
    formData.append('type', 'DISCREPANCY');
    formData.append('severity', severity);
    formData.append('expected_qty', lotData.planned_qty);
    formData.append('actual_qty', incomingQty || lotData.planned_qty);
    formData.append('defect_category', defectCategory);
    formData.append('comments', comments);
    
    for (let i = 0; i < photos.length; i++) {
        formData.append('evidence_photos', photos[i]);
    }

    try {
        const res = await axios.post(`${BASE_URL}/api/wip-dashboard/tickets`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
        });
        if (res.data.success) {
            Swal.fire("Ticket Raised", `Temporary Ticket Raised! Lot ${lotData.lot_id} is blocked until resolved.`, "success");
            resetForm();
        }
    } catch (err) {
        Swal.fire("Error", err.response?.data?.message || "Error raising ticket.", "error");
    }
  };

  const resetForm = () => {
    setBarcode('');
    setLotData(null);
    setOutgoingQty('');
    setIncomingQty('');
    setQualityStatus('Pass');
    setMode('scan');
  };

  return (
    <div className="d-flex dashboard">
      <style>{`
        .custom-input {
          padding: 14px 16px;
          border-radius: 12px;
          border: 1px solid #cbd5e1;
          outline: none;
          font-size: 15px;
          width: 100%;
          transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }
        .custom-input:focus {
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }
        .custom-btn {
          padding: 14px 24px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          width: 100%;
        }
        .custom-btn-primary {
          background-color: #8b5cf6;
          color: white;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.2);
        }
        .custom-btn-primary:hover {
          background-color: #7c3aed;
        }
        .custom-btn-secondary {
          background-color: #f1f5f9;
          color: #475569;
          border: 1px solid #cbd5e1;
        }
        .custom-btn-secondary:hover {
          background-color: #e2e8f0;
        }
        .custom-btn-danger {
          background-color: #ef4444;
          color: white;
          box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.2);
        }
        .custom-btn-danger:hover {
          background-color: #dc2626;
        }
        .info-card {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #e2e8f0;
          margin-bottom: 24px;
        }
      `}</style>

      {/* ── MAIN CONTENT AREA ── */}
      <main className="w-100 p-0" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        
        {/* ── STANDALONE KIOSK BANNER ── */}
        <div style={{
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)", // Purple/Indigo gradient
          padding: "24px 40px", color: "#fff",
          boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)",
          textAlign: "center"
        }}>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-upc-scan me-3"></i> FIBREBOND Handover Kiosk
          </h1>
          <p style={{ margin: "6px 0 0", opacity: 0.9, fontSize: 16, fontWeight: 500 }}>
            Scan a barcode to dispatch or receive a production lot.
          </p>
        </div>

        <div style={{ padding: "80px 40px 40px 40px", flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          
          {/* ── FORM CARD ── */}
          <div style={{ 
            backgroundColor: "#ffffff", borderRadius: "20px", padding: "40px", 
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)", 
            border: "1px solid #e2e8f0", width: "100%", maxWidth: "650px" 
          }}>

              {/* SCANNER SECTION */}
              {mode === 'scan' && (
                <form onSubmit={fetchLot}>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Scan Barcode / QR</label>
                    <div style={{ position: "relative" }}>
                      <i className="bi bi-upc" style={{ position: "absolute", left: 16, top: 16, fontSize: 18, color: "#64748b" }}></i>
                      <input 
                        type="text" 
                        className="custom-input"
                        style={{ paddingLeft: 46 }}
                        placeholder="e.g. BATCH-01-LOT-002"
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>
                  <button type="submit" className="custom-btn custom-btn-primary">
                    Find Lot Details
                  </button>
                </form>
              )}

              {/* OUTGOING MODE */}
              {mode === 'outgoing' && lotData && (
                <form onSubmit={handleOutgoingSubmit}>
                  <div className="info-card" style={{ borderLeft: "4px solid #8b5cf6" }}>
                    <h5 style={{ fontWeight: 700, color: "#0f172a", marginBottom: 12, fontSize: 16 }}>Outgoing Handover</h5>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 14 }}>
                      <div><span style={{ color: "#64748b" }}>Lot:</span> <strong style={{ color: "#0f172a" }}>{lotData.lot_id}</strong></div>
                      <div><span style={{ color: "#64748b" }}>Batch:</span> <strong style={{ color: "#0f172a" }}>{lotData.batch_id}</strong></div>
                      <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "#64748b" }}>Process:</span> <strong style={{ color: "#8b5cf6" }}>{lotData.current_process_name}</strong></div>
                      <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "#64748b" }}>Planned Qty:</span> <strong style={{ color: "#0f172a" }}>{lotData.planned_qty}</strong></div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Completed / Good Quantity</label>
                    <input 
                      type="number" 
                      className="custom-input"
                      value={outgoingQty}
                      onChange={(e) => setOutgoingQty(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div style={{ display: "flex", gap: 12 }}>
                    <button type="button" className="custom-btn custom-btn-secondary" onClick={resetForm}>Cancel</button>
                    <button type="submit" className="custom-btn custom-btn-primary">Confirm Transfer</button>
                  </div>
                </form>
              )}

              {/* INCOMING MODE */}
              {mode === 'incoming' && lotData && (
                <form onSubmit={handleIncomingSubmit}>
                  <div className="info-card" style={{ borderLeft: "4px solid #f59e0b", backgroundColor: "#fffbeb", borderColor: "#fde68a" }}>
                    <h5 style={{ fontWeight: 700, color: "#b45309", marginBottom: 12, fontSize: 16 }}>Incoming Verification</h5>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 14 }}>
                      <div><span style={{ color: "#92400e" }}>Lot:</span> <strong style={{ color: "#78350f" }}>{lotData.lot_id}</strong></div>
                      <div><span style={{ color: "#92400e" }}>From:</span> <strong style={{ color: "#78350f" }}>{lotData.current_process_name}</strong></div>
                      <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "#92400e" }}>Expected Qty:</span> <strong style={{ color: "#b45309", fontSize: 16 }}>{lotData.planned_qty}</strong></div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Actual Received Qty</label>
                    <input 
                      type="number" 
                      className="custom-input"
                      value={incomingQty}
                      onChange={(e) => setIncomingQty(e.target.value)}
                      required
                    />
                    {incomingQty !== '' && parseInt(incomingQty) !== parseInt(lotData.planned_qty) && (
                      <div style={{ color: "#ef4444", marginTop: 8, fontSize: 13, fontWeight: 600 }}>
                        <i className="bi bi-exclamation-triangle me-1"></i> Shortage detected. Ticket will be required.
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Visual Quality Check</label>
                    <select 
                      className="custom-input"
                      style={{ 
                        backgroundColor: qualityStatus === 'Fail' ? '#fef2f2' : '#f0fdf4',
                        borderColor: qualityStatus === 'Fail' ? '#fca5a5' : '#bbf7d0',
                        color: qualityStatus === 'Fail' ? '#991b1b' : '#166534',
                        fontWeight: 600
                      }}
                      value={qualityStatus}
                      onChange={(e) => setQualityStatus(e.target.value)}
                    >
                      <option value="Pass">Pass - Good Condition</option>
                      <option value="Fail">Fail - Defective</option>
                    </select>
                  </div>

                  <div style={{ display: "flex", gap: 12 }}>
                    <button type="button" className="custom-btn custom-btn-secondary" onClick={resetForm}>Cancel</button>
                    <button type="submit" className={`custom-btn ${(qualityStatus === 'Fail' || (incomingQty && parseInt(incomingQty) !== parseInt(lotData.planned_qty))) ? 'custom-btn-danger' : 'custom-btn-primary'}`} style={{ backgroundColor: !(qualityStatus === 'Fail' || (incomingQty && parseInt(incomingQty) !== parseInt(lotData.planned_qty))) ? '#10b981' : undefined, boxShadow: !(qualityStatus === 'Fail' || (incomingQty && parseInt(incomingQty) !== parseInt(lotData.planned_qty))) ? '0 4px 6px -1px rgba(16, 185, 129, 0.2)' : undefined }}>
                      {(qualityStatus === 'Fail' || (incomingQty && parseInt(incomingQty) !== parseInt(lotData.planned_qty))) ? 'Raise Ticket' : 'Accept Handover'}
                    </button>
                  </div>
                </form>
              )}

              {/* TICKET MODE */}
              {mode === 'ticket' && lotData && (
                <form onSubmit={handleTicketSubmit}>
                  <div className="info-card" style={{ borderLeft: "4px solid #ef4444", backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
                    <h5 style={{ fontWeight: 700, color: "#b91c1c", marginBottom: 4, fontSize: 16 }}>
                      <i className="bi bi-shield-exclamation me-2"></i> Raise Discrepancy Ticket
                    </h5>
                    <p style={{ margin: 0, fontSize: 13, color: "#991b1b" }}>Actual quantity or quality does not match the outgoing handover.</p>
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                    <div>
                      <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Severity</label>
                      <select className="custom-input" value={severity} onChange={(e) => setSeverity(e.target.value)} required>
                        <option value="Minor">Minor</option>
                        <option value="Major">Major</option>
                        <option value="Critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Defect Category</label>
                      <select className="custom-input" value={defectCategory} onChange={(e) => setDefectCategory(e.target.value)} required>
                        <option value="">Select...</option>
                        <option value="Short Quantity">Short Quantity</option>
                        <option value="Stitching Error">Stitching Error</option>
                        <option value="Colour Mismatch">Colour/Design Mismatch</option>
                        <option value="Dirt/Scratch">Dirt / Scratch</option>
                        <option value="Missing Process">Incomplete Previous Process</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Comments / Reason</label>
                    <textarea 
                      className="custom-input"
                      rows="2" 
                      placeholder="Describe the issue..."
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    ></textarea>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#334155", marginBottom: 8, fontSize: 14 }}>Evidence Photo(s) *</label>
                    <input 
                      type="file" 
                      className="custom-input"
                      style={{ padding: "10px 16px", borderColor: "#fca5a5", backgroundColor: "#fef2f2" }}
                      accept="image/*"
                      multiple
                      onChange={(e) => setPhotos(e.target.files)}
                      required
                    />
                    <small style={{ color: "#ef4444", fontWeight: 600, display: "block", marginTop: 8, fontSize: 13 }}>
                      <i className="bi bi-camera-fill me-1"></i> Mandatory proof of shortage or defect.
                    </small>
                  </div>

                  <div style={{ display: "flex", gap: 12 }}>
                    <button type="button" className="custom-btn custom-btn-secondary" onClick={resetForm}>Cancel</button>
                    <button type="submit" className="custom-btn custom-btn-danger">Submit Ticket</button>
                  </div>
                </form>
              )}
          </div>
        </div>
      </main>
    </div>
  );
}
